// demo/index.js — the Comprehend Live demo harness.
// Live voice + live agent over scripted data: ephemeral keys and the
// realtime conversation are REAL; schedule, histories, compliance results,
// and CDS nudges are canned and fire on a compressed timeline. The demo
// fakes data, never behavior. Guide: docs/comprehend-live-demo-guide.md.
import { liveStore, QUEUE_KINDS, scoreToGrade } from '../liveStore';
import { registerRecorderActions } from '../recorderBridge';
import {
  IDS,
  DEMO_PATIENTS,
  DEMO_HISTORIES,
  DEMO_DETAILS,
  JIMMY_CDS_NUDGES,
  buildDemoSessions,
  buildDemoSchedule,
} from './demoData';

export const DEMO_FLAG_KEY = 'comprehendpt_live_demo';

export const isDemoMode = () => {
  try {
    if (window.location.hash.includes('liveDemo')) {
      localStorage.setItem(DEMO_FLAG_KEY, '1');
      return true;
    }
    return localStorage.getItem(DEMO_FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Wrap the real ApiClient: real keys/voice, canned data.
 */
const buildDemoApiClient = (real) => ({
  // LIVE: the voice/agent session is real.
  getSynapseKey: (...args) => real.getSynapseKey(...args),
  // LIVE: ambient passes through to the REAL backend brain. Demo convo ids
  // don't resolve server-side, so the brain runs listen-only (actions, no
  // chart commits) — the real pipeline is testable while the scripted
  // nudges/queue stay untouched.
  ambientChunk: (payload) => real.ambientChunk(payload),
  // Dev flag passes through (getter — profile may load after wrap) so the
  // DEMO chip stays visible to exit demo mode.
  get isDev() {
    return real.isDev;
  },

  // CANNED everything else.
  patients: DEMO_PATIENTS,
  getUserConversations: async () => ({
    conversations: liveStore.getState().sessions.map((s) => ({
      conversationId: s.conversationId,
      conversationName: s.name,
      group: s.group,
      createdAt: s.createdAt,
      status: s.status,
    })),
  }),
  getAgentQueue: async () => ({ items: [], sessions: [] }),
  getConversationDetails: async (conversationId) => {
    const fixture = DEMO_DETAILS[conversationId];
    if (!fixture) return { error: 'not found' };
    const session = liveStore
      .getState()
      .sessions.find((s) => s.conversationId === conversationId);
    return { ...fixture, name: session?.name };
  },
  getPatientHistory: async (group) =>
    DEMO_HISTORIES[group] || { error: 'not found' },
  agentApplyNoteFix: async (conversationId, instruction) => ({
    result: 'success',
    fieldName: 'Objective',
    updated: instruction,
    undo: { fieldName: 'Objective', previous: '' },
  }),
  editConversation: async () => ({ result: 'success' }),
});

export const setupDemo = (realApiClient) => {
  // Floor to the minute so every demo visit lands exactly on a minute (never
  // between) — demoData offsets are whole-minute multiples of this.
  const t0 = Math.floor(Date.now() / 60000) * 60000;
  const apiClient = buildDemoApiClient(realApiClient);
  const timers = [];
  const after = (ms, fn) => timers.push(setTimeout(fn, ms));

  // Visit-ended hook the Pulse calls: canned compliance lands instantly.
  const finishNote = async ({ conversationId, patientName }) => {
    const fixture = DEMO_DETAILS[conversationId];
    if (!fixture) return;
    liveStore.upsertSession({
      conversationId,
      status: 'Transcription Complete!',
      score: fixture.score,
      grade: scoreToGrade(fixture.score),
    });
    (fixture.requirement_evaluations || [])
      .filter((e) => !['fully met', 'not applicable'].includes(e.status))
      .forEach((e) =>
        liveStore.addQueueItem({
          kind: QUEUE_KINDS.NUDGE,
          source: 'compliance',
          severity: e.is_required ? 'urgent' : 'warn',
          conversationId,
          patientName,
          title: e.requirement_text,
          detail: e.status,
          proposedText: e.suggestion || null,
        }),
      );
    liveStore.addQueueItem({
      kind: QUEUE_KINDS.REVIEW,
      source: 'compliance',
      severity: fixture.score < 60 ? 'urgent' : 'info',
      conversationId,
      patientName,
      title: `${patientName} — note ready to review`,
      detail: `Grade ${scoreToGrade(fixture.score)} (${fixture.score})`,
    });
  };

  const start = () => {
    liveStore.setDemo(true);

    // Fake recorder so the Pulse's auto-switch drives the rail.
    registerRecorderActions({
      startRecording: async (ids) => {
        liveStore.setRecordingSessions(ids);
        return { ok: true };
      },
      stopRecording: async (ids) => {
        const current = liveStore
          .getState()
          .sessions.filter((s) => s.recording)
          .map((s) => s.conversationId)
          .filter((id) => !(ids || []).includes(id));
        liveStore.setRecordingSessions(current);
        return { ok: true };
      },
      getRecordingIds: () =>
        liveStore
          .getState()
          .sessions.filter((s) => s.recording)
          .map((s) => s.conversationId),
    });

    // Opening beat: "noticed the schedule was stale, pulled it".
    after(2000, () => {
      liveStore.setState({
        sessions: buildDemoSessions(t0),
        hydratedAt: new Date().toISOString(),
      });
      liveStore.setSchedule(buildDemoSchedule(t0));
      liveStore.addReceipt({
        text: 'Schedule was 9h stale — pulled today: 4 visits, 2 overlapping pairs',
        why: 'Pulse: no calendar sync since last night',
      });
      // Yesterday's leftover work seeds the queue (Janie's C note).
      const janie = DEMO_DETAILS[IDS.janie];
      janie.requirement_evaluations
        .filter((e) => !['fully met', 'not applicable'].includes(e.status))
        .forEach((e) =>
          liveStore.addQueueItem({
            kind: QUEUE_KINDS.NUDGE,
            source: 'compliance',
            severity: 'warn',
            conversationId: IDS.janie,
            patientName: 'Janie Wilson',
            title: e.requirement_text,
            detail: e.status,
            proposedText: e.suggestion || null,
          }),
        );
      liveStore.addQueueItem({
        kind: QUEUE_KINDS.REVIEW,
        source: 'compliance',
        severity: 'urgent',
        conversationId: IDS.janie,
        patientName: 'Janie Wilson',
        title: 'Janie Wilson — note ready to review',
        detail: 'Grade C (58)',
      });
    });

    // ~30s into Jimmy's slot: CDS nudges from his history + RTM data.
    after(75 * 1000, () => {
      liveStore.addReceipt({
        text: "Reviewed Jimmy's chart — RTM adherence dropped, ROM progress stalled",
        why: 'Pulse: visit started, history + RTM scan',
      });
      JIMMY_CDS_NUDGES.forEach((n) => liveStore.addQueueItem({ ...n }));
    });

    // Live grades tick mid-slot.
    after(95 * 1000, () => {
      liveStore.upsertSession({
        conversationId: IDS.jimmy,
        grade: 'B',
        score: 68,
      });
      liveStore.upsertSession({
        conversationId: IDS.sarah,
        grade: 'A',
        score: 88,
      });
    });
    after(200 * 1000, () => {
      liveStore.upsertSession({
        conversationId: IDS.marcus,
        grade: 'B',
        score: 72,
      });
      liveStore.upsertSession({
        conversationId: IDS.elena,
        grade: 'A',
        score: 90,
      });
    });

    // EMR pushes resolve on their own (working → resolved).
    const unsubscribe = liveStore.subscribe((s) => {
      s.queue
        .filter(
          (q) =>
            q.status === 'pending' &&
            (q.title || '').includes('ready to push') &&
            !q._demoPushScheduled,
        )
        .forEach((q) => {
          liveStore.updateQueueItem(q.id, {
            _demoPushScheduled: true,
            status: 'working',
            title: q.title.replace("'s note is ready to push", "'s note → EMR"),
            detail: null,
          });
          after(4000, () =>
            liveStore.updateQueueItem(q.id, {
              status: 'resolved',
              detail: 'Pushed — open your EMR to sign',
            }),
          );
        });
    });
    timers.push({ unsubscribe });
  };

  const stop = () => {
    timers.forEach((t) =>
      typeof t === 'object' ? t.unsubscribe() : clearTimeout(t),
    );
  };

  return { apiClient, finishNote, start, stop };
};
